import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { applicationLabel } from '@aeris/catalog'
import { Badge, Button, Combobox, EmptyState, Spinner } from '@aeris/ui'

import {
  fetchApplicationModels,
  fetchProductApplications,
  linkProductApplication,
  unlinkProductApplication,
} from '../lib/api.ts'
import { useDebounced } from '../lib/use-debounced.ts'

/**
 * What this part fits, on the product's own screen (F031).
 *
 * It lives here rather than on Aplicações because this is where somebody is
 * holding the part. The register is the vocabulary; using it is a fact about
 * the product, beside its unit and its NCM.
 *
 * The picker searches models across ALL brands, deliberately. Making somebody
 * choose a brand first is right when they are building the register — the tree
 * is the whole point there — and wrong here, where they already know they are
 * looking at an A20 and the brand is something they would have to remember
 * rather than something they are deciding.
 */

export type ProductApplicationsProps = {
  productId: string
  canWrite: boolean
}

export const ProductApplications = ({
  productId,
  canWrite,
}: ProductApplicationsProps): ReactNode => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState<string | null>(null)

  // The same 150ms the PDV settled on in F028: above what a scanner takes, and
  // below what a person notices.
  const debounced = useDebounced(search, 150)

  const applications = useQuery({
    queryKey: ['product-applications', productId],
    queryFn: async ({ signal }) =>
      await fetchProductApplications(productId, signal),
  })

  const models = useQuery({
    queryKey: ['application-models', 'search', debounced],
    queryFn: async ({ signal }) =>
      await fetchApplicationModels({ search: debounced, limit: 20 }, signal),
    enabled: canWrite,
  })

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['product-applications', productId],
    })
  }

  const link = useMutation({
    mutationFn: async (modelId: string) => {
      await linkProductApplication(productId, { modelId, note: null })
    },
    onSuccess: async () => {
      setChosen(null)
      setSearch('')
      await invalidate()
    },
  })

  const unlink = useMutation({
    mutationFn: async (modelId: string) => {
      await unlinkProductApplication(productId, modelId)
    },
    onSuccess: invalidate,
  })

  const rows = applications.data?.applications ?? []

  return (
    <div className="flex flex-col gap-3">
      {canWrite ? (
        <Combobox
          value={chosen}
          onValueChange={(value) => {
            setChosen(value)
            if (value !== null) link.mutate(value)
          }}
          search={search}
          onSearchChange={setSearch}
          loading={models.isFetching}
          placeholder="Buscar um modelo — nome, marca ou referência"
          aria-label="Adicionar aplicação"
          items={(models.data?.models ?? []).map((model) => ({
            value: model.id,
            // Brand-first, because "Fit" alone is ambiguous across
            // manufacturers and a list of bare model names is a list somebody
            // has to open one by one.
            label: applicationLabel(model),
          }))}
        />
      ) : null}

      {applications.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nenhuma aplicação"
          description="Este produto ainda não diz em que serve, então não aparece na busca por modelo."
        />
      ) : (
        <ul className="flex flex-wrap gap-2">
          {rows.map((row) => (
            <li key={row.modelId}>
              <Badge tone="neutral">
                <span>
                  {row.brandName} {row.modelName}
                  {row.note === null ? null : (
                    <span className="text-(--text-muted)"> — {row.note}</span>
                  )}
                </span>
                {canWrite ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    // Named, because a row of identical bin icons is a row a
                    // screen reader announces as "button, button, button".
                    aria-label={`Remover ${row.brandName} ${row.modelName}`}
                    onClick={() => {
                      unlink.mutate(row.modelId)
                    }}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                ) : null}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
